import { Link, useParams } from "react-router-dom";
import SeoMeta from "@/components/feature/SeoMeta";
import ArticleCard from "@/components/feature/ArticleCard";
import SectionHeading from "@/components/feature/SectionHeading";
import { formatHeat, getArticlesByTopic, getTopicBySlug, sortByLatest } from "@/lib/articles";
import { topics } from "@/mocks/topics";
import { trending } from "@/mocks/trending";

export default function Topic() {
  const { slug = "" } = useParams();
  const topic = getTopicBySlug(slug);
  const list = sortByLatest(getArticlesByTopic(slug));

  if (!topic) {
    return (
      <div className="mx-auto flex max-w-2xl flex-col items-center px-4 py-24 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
          <i className="ri-emotion-sad-line" aria-hidden="true" />
        </span>
        <h1 className="mt-6 font-heading text-2xl font-black text-foreground-950">没有找到这个栏目</h1>
        <p className="mt-3 text-sm text-foreground-600">栏目可能已经调整，去首页看看最新的爆料吧。</p>
        <Link
          to="/"
          className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-label font-bold text-background-50"
        >
          返回首页
          <i className="ri-arrow-right-line" aria-hidden="true" />
        </Link>
      </div>
    );
  }

  const breadcrumb = [
    { "@type": "ListItem", position: 1, name: "首页", item: "/" },
    { "@type": "ListItem", position: 2, name: topic.name, item: `/topic/${topic.slug}` },
  ];

  return (
    <>
      <SeoMeta
        title={`${topic.name}爆料 - 独家大瓜最新首发追踪`}
        description={`${topic.desc}共收录 ${topic.count} 条${topic.name}相关爆料，全部标注可信度等级并持续更新追踪时间线。`}
        keywords={`${topic.name},${topic.name}爆料,独家大瓜,独家大瓜最新,大瓜爆料`}
        path={`/topic/${topic.slug}`}
        image={topic.cover}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${topic.name}爆料`,
            description: topic.desc,
            inLanguage: "zh-CN",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: breadcrumb,
          },
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            itemListElement: list.slice(0, 10).map((a, index) => ({
              "@type": "ListItem",
              position: index + 1,
              name: a.title,
              url: `/article/${a.slug}`,
            })),
          },
        ]}
      />

      <nav aria-label="面包屑" className="border-b border-background-200 bg-background-100">
        <ol className="shell flex flex-wrap items-center gap-2 px-4 py-3 text-xs text-foreground-500 md:px-6">
          <li>
            <Link to="/" className="transition-colors hover:text-primary-600 cursor-pointer">
              首页
            </Link>
          </li>
          <li aria-hidden="true">
            <i className="ri-arrow-right-s-line" />
          </li>
          <li className="font-medium text-foreground-800">{topic.name}</li>
        </ol>
      </nav>

      <section className="border-b border-background-200 bg-background-100 px-4 py-10 md:px-6 md:py-14">
        <div className="shell flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="max-w-2xl">
            <span className="text-xs font-label font-bold uppercase tracking-[0.25em] text-primary-600">
              {topic.en}
            </span>
            <h1 className="mt-3 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
              {topic.name}爆料
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-[15px]">{topic.desc}</p>
          </div>
          <div className="flex gap-3">
            <div className="rounded-lg border border-background-200 bg-background-50 px-5 py-4 text-center">
              <span className="block font-heading text-2xl font-black text-primary-600">{topic.count}</span>
              <span className="mt-1 block text-[11px] text-foreground-500">累计条目</span>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 px-5 py-4 text-center">
              <span className="block font-heading text-2xl font-black text-accent-600">{list.length}</span>
              <span className="mt-1 block text-[11px] text-foreground-500">在库更新</span>
            </div>
          </div>
        </div>
      </section>

      <div className="shell grid grid-cols-1 gap-8 px-4 py-12 md:px-6 md:py-16 lg:grid-cols-3 lg:gap-10">
        <div className="lg:col-span-2">
          <SectionHeading eyebrow="Articles" title={`${topic.name} · 全部爆料`} />
          {list.length > 0 ? (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              {list.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          ) : (
            <p className="rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center text-sm text-foreground-500">
              这个栏目暂时还没有收录内容
            </p>
          )}

          <div className="mt-10 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-links-line text-primary-500" aria-hidden="true" />
              浏览其他栏目
            </h2>
            <div className="mt-4 flex flex-wrap gap-2.5">
              {topics
                .filter((t) => t.slug !== topic.slug)
                .map((t) => (
                  <Link
                    key={t.slug}
                    to={`/topic/${t.slug}`}
                    className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 px-4 py-1.5 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
                  >
                    {t.name}
                    <i className="ri-arrow-right-up-line text-xs" aria-hidden="true" />
                  </Link>
                ))}
            </div>
          </div>
        </div>

        <aside className="lg:col-span-1">
          <div className="sticky top-28 rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="mb-4 flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
              <i className="ri-fire-line text-primary-500" aria-hidden="true" />
              今日热榜
            </h2>
            <ol className="flex flex-col gap-3.5">
              {trending.slice(0, 6).map((item) => (
                <li key={item.slug}>
                  <Link to={`/article/${item.slug}`} className="group flex cursor-pointer items-start gap-3">
                    <span
                      className={`mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md font-label text-xs font-black ${
                        item.rank <= 3 ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-700"
                      }`}
                    >
                      {item.rank}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm leading-snug text-foreground-900 transition-colors group-hover:text-primary-600 line-clamp-2">
                        {item.title}
                      </span>
                      <span className="mt-1 block text-[11px] text-foreground-500">
                        {item.tag} · {formatHeat(item.heat)}
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
            <Link
              to="/about#contact"
              className="mt-5 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-4 py-2.5 text-sm font-label font-bold text-background-50 transition-colors hover:bg-primary-600"
            >
              订阅爆料速递
            </Link>
          </div>
        </aside>
      </div>
    </>
  );
}